import type { Manifest, Transition } from "../manifest/schema.js";

/** A directed, playable edge. Derived (reverse) edges play `<video>.rev.*`. */
export interface Edge {
  id: string;
  from: string;
  to: string;
  video: string;
  poster?: string;
  durationSec: number;
  label: string;
  derivedReverse: boolean;
}

function forwardEdge(t: Transition): Edge {
  return { id: t.id, from: t.from, to: t.to, video: t.video, poster: t.poster, durationSec: t.durationSec, label: t.label, derivedReverse: false };
}

function reverseEdge(t: Transition): Edge {
  return { id: `${t.id}--rev`, from: t.to, to: t.from, video: `${t.video}.rev`, durationSec: t.durationSec, label: t.reverseLabel ?? t.label, derivedReverse: true };
}

/** Directed navigation graph built from the manifest's transitions. */
export class NavGraph {
  private readonly out = new Map<string, Edge[]>();
  private readonly locationIds = new Set<string>();
  private readonly shopEntry = new Map<string, string>();

  constructor(manifest: Pick<Manifest, "locations" | "transitions" | "shops">) {
    for (const l of manifest.locations) {
      this.locationIds.add(l.id);
      this.out.set(l.id, []);
    }
    for (const s of manifest.shops) this.shopEntry.set(s.id, s.entryLocationId);
    const explicit = new Set(manifest.transitions.map((t) => `${t.from}>${t.to}`));
    for (const t of manifest.transitions) {
      this.add(forwardEdge(t));
      if (t.reversible && !explicit.has(`${t.to}>${t.from}`)) this.add(reverseEdge(t));
    }
  }

  private add(e: Edge): void {
    const list = this.out.get(e.from);
    if (list) list.push(e);
  }

  has(locationId: string): boolean {
    return this.locationIds.has(locationId);
  }

  edgesFrom(locationId: string): readonly Edge[] {
    return this.out.get(locationId) ?? [];
  }

  allEdges(): Edge[] {
    return [...this.out.values()].flat();
  }

  edge(from: string, to: string): Edge | undefined {
    return this.edgesFrom(from).find((e) => e.to === to);
  }

  /** Cheapest route by total clip duration (Dijkstra). `[]` when already there; `undefined` when unreachable. */
  path(from: string, to: string): Edge[] | undefined {
    if (!this.has(from) || !this.has(to)) return undefined;
    if (from === to) return [];
    const dist = new Map<string, number>([[from, 0]]);
    const prev = new Map<string, Edge>();
    const todo = new Set<string>([from]);
    while (todo.size > 0) {
      let best: string | undefined;
      for (const id of todo) if (best === undefined || (dist.get(id) ?? Infinity) < (dist.get(best) ?? Infinity)) best = id;
      if (best === undefined) break;
      todo.delete(best);
      if (best === to) break;
      const d = dist.get(best) ?? 0;
      for (const e of this.edgesFrom(best)) {
        const nd = d + e.durationSec;
        if (nd < (dist.get(e.to) ?? Infinity)) {
          dist.set(e.to, nd);
          prev.set(e.to, e);
          todo.add(e.to);
        }
      }
    }
    if (!prev.has(to)) return undefined;
    const route: Edge[] = [];
    for (let cur = to; cur !== from; ) {
      const e = prev.get(cur);
      if (!e) return undefined;
      route.unshift(e);
      cur = e.from;
    }
    return route;
  }

  /** "Take me to shop X": route to the shop's entry location. */
  pathToShop(from: string, shopId: string): Edge[] | undefined {
    const entry = this.shopEntry.get(shopId);
    return entry === undefined ? undefined : this.path(from, entry);
  }

  /** All locations reachable from `from` (including itself). */
  reachableFrom(from: string): Set<string> {
    const seen = new Set<string>();
    if (!this.has(from)) return seen;
    const stack = [from];
    while (stack.length > 0) {
      const id = stack.pop() as string;
      if (seen.has(id)) continue;
      seen.add(id);
      for (const e of this.edgesFrom(id)) stack.push(e.to);
    }
    return seen;
  }

  /** Locations from which `target` can be reached (so nobody gets trapped). */
  canReach(target: string): Set<string> {
    const ok = new Set<string>();
    for (const id of this.locationIds) if (this.reachableFrom(id).has(target)) ok.add(id);
    return ok;
  }
}
